import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  ActivationEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  ActivatedRoute,
} from '@angular/router';
import { AuthenticatorService } from '@aws-amplify/ui-angular';
import { Auth } from 'aws-amplify';
import { AuthService } from 'src/app/auth-o3/shared/services/auth.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit {
  environment = '';
  signedIn = false;
  user: string = undefined;
  email: string = undefined;
  identityProviders = [];
  requestedScope: string = '';
  redirectUrl: string = '';

  ssoEmail = '';

  public formFields = {
    signIn: {
      username: {
        placeholder: 'Enter Username',
        isRequired: true,
        label: 'Username:',
      },
      password: {
        placeholder: 'Enter Password',
        isRequired: true,
        label: 'Password:',
      },
    },
    resetPassword: {
      username: {
        placeholder: 'Username',
        isRequired: true,
        label: 'Username:',
      },
    },
    setupTOTP: {
      QR: {
        totpIssuer: 'inSPire Platform',
      },
    },
  };

  constructor(
    private authService: AuthService,
    public authenticator: AuthenticatorService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.authService.onMfaEnvironment.subscribe((environment) => {
      this.environment = environment;
      this.formFields.setupTOTP.QR.totpIssuer =
        'inSPire Platform - ' + environment;
    });

    this.authService.onUserSession.subscribe(async (session) => {
      this.signedIn = session?.isValid();
      this.user =
        session?.isValid() && session.idToken.payload['cognito:username'];
      this.email = session?.isValid() && session.idToken.payload.email;

      if (this.signedIn) {
        const redirectAfterOAuth = sessionStorage.getItem('redirectAfterOAuth');
        if (redirectAfterOAuth) {
          sessionStorage.removeItem('redirectAfterOAuth');
          this.router.navigateByUrl(redirectAfterOAuth);
        } else {
          await this.checkScopesAndRedirect();
        }
      }
    });

    this.setupHubListener();

    this.authService.onAuthSSO.subscribe((identityProviders) => {
      this.identityProviders = identityProviders;
    });
  }

  async ngOnInit() {
    this.route.queryParams.subscribe(async (params) => {
      this.requestedScope = params['scope'] || '';
      this.redirectUrl =
        decodeURIComponent(params['redirect'] || '') || '/portal';

      if (this.requestedScope) {
        await this.configureOAuthWithScope(this.requestedScope);
      } else {
        setTimeout(async () => {
          try {
            const currentSession = await Auth.currentSession();
            if (currentSession.isValid()) {
              this.router.navigateByUrl('/portal');
            }
          } catch (error) {}
        }, 1000);
      }
    });
  }

  private handleSuccessfulLogin() {
    if (this.redirectUrl.startsWith('http')) {
      window.open(this.redirectUrl, '_blank', 'noopener,noreferrer');
      this.router.navigateByUrl('/portal');
    } else {
      this.router.navigateByUrl(this.redirectUrl);
    }
  }

  private async checkScopesAndRedirect() {
    if (!this.requestedScope) {
      this.handleSuccessfulLogin();
      return;
    }

    try {
      const currentSession = await Auth.currentSession();
      const accessToken = currentSession.getAccessToken().getJwtToken();
      const accessTokenPayload = JSON.parse(atob(accessToken.split('.')[1]));

      const currentScopes = accessTokenPayload.scope
        ? accessTokenPayload.scope.split(' ')
        : [];

      if (currentScopes.includes(this.requestedScope)) {
        this.handleSuccessfulLogin();
      } else {
        await Auth.signOut({ global: true });
      }
    } catch (error) {}
  }

  private async configureOAuthWithScope(scope: string) {
    await this.retryConfigureOAuth(scope, 0);
  }

  private async retryConfigureOAuth(scope: string, attempt: number) {
    try {
      const authServers = this.authService.authServers;
      if (!authServers || authServers.length === 0) {
        if (attempt < 10) {
          setTimeout(() => this.retryConfigureOAuth(scope, attempt + 1), 500);
        }
        return;
      }

      const authServer = authServers[0];
      const devPort = '4200';
      const redirectBase = window.location.hostname.includes('localhost')
        ? 'http://localhost:' + devPort
        : 'https://' + window.location.hostname;

      AuthService.configureOAuth(authServer, redirectBase, [scope]);

      try {
        await Auth.signOut({ global: true });
        await new Promise((resolve) => setTimeout(resolve, 500));
      } catch (error) {}

      setTimeout(() => {
        Auth.federatedSignIn();
      }, 1000);
    } catch (error) {}
  }

  private async setupHubListener() {
    const { Hub } = await import('aws-amplify');
    Hub.listen('auth', (data) => {
      if (data.payload.event === 'signIn') {
        setTimeout(async () => {
          try {
            const currentSession = await Auth.currentSession();
            if (currentSession.isValid()) {
              await this.checkScopesAndRedirect();
            }
          } catch (error) {}
        }, 1000);
      }
    });
  }

  handleSignInClick(provider: string) {
    this.authenticator.toFederatedSignIn({
      provider: provider,
    });
  }

  onSSOEmailSubmit() {
    if (!this.ssoEmail || !this.ssoEmail.includes('@')) {
      return;
    }

    const provider = this.authService.redirectToSSOWithEmail(this.ssoEmail);

    if (provider) {
      console.log(
        `Redirecting to ${provider} for domain: ${this.ssoEmail.split('@')[1]}`
      );
    } else {
      console.log(
        `No SSO provider configured for domain: ${this.ssoEmail.split('@')[1]}`
      );
    }
  }

  onSSOEmailChange() {}

  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
    }
    if (
      event instanceof NavigationEnd ||
      event instanceof NavigationCancel ||
      event instanceof NavigationError
    ) {
    }
    if (event instanceof ActivationEnd) {
    }
  }
}
